import { Badge } from "../primitives";
import { bandByKey, type RiskBand } from "@/config/riskBands";
import type { Action, ManagedListType, Outcome } from "@/config/filters";

export function RiskBandBadge({ band }: { band: RiskBand }) {
  const def = bandByKey(band);
  return <Badge tone={band}>{def.label}</Badge>;
}

const ACTION_TONE: Record<Action, "low" | "watch" | "suspicious" | "high"> = {
  allow: "low",
  observe: "watch",
  challenge_candidate: "suspicious",
  block_candidate: "high",
};

const ACTION_LABEL: Record<Action, string> = {
  allow: "Allow",
  observe: "Observe",
  challenge_candidate: "Challenge",
  block_candidate: "Block",
};

export function ActionBadge({ action }: { action: Action }) {
  return <Badge tone={ACTION_TONE[action]}>{ACTION_LABEL[action]}</Badge>;
}

const MANAGED_LIST_LABEL: Record<ManagedListType, string> = {
  botnetcc: "Botnet C&C",
  malware: "Malware",
  "open-proxies": "Open proxy",
  anonymizer: "Anonymiser",
  vpn: "VPN",
};

export function ManagedListBadge({ type, title }: { type: ManagedListType; title?: string }) {
  const tone =
    type === "botnetcc" || type === "malware" ? "high" :
    type === "open-proxies" || type === "anonymizer" ? "suspicious" :
    "watch";
  return (
    <span title={title ?? `Cloudflare managed list: ${type}`}>
      <Badge tone={tone}>{MANAGED_LIST_LABEL[type] ?? type}</Badge>
    </span>
  );
}

export function OutcomeBadge({ outcome }: { outcome: Outcome }) {
  const tone = outcome === "failure" ? "high" : outcome === "success" ? "good" : "neutral";
  return <Badge tone={tone}>{outcome}</Badge>;
}

export function VerifiedBotBadge({ verified }: { verified: boolean }) {
  return verified ? <Badge tone="good">verified</Badge> : <span className="text-fg-subtle text-xs">—</span>;
}

export function BotScoreBadge({ score }: { score: number | null | undefined }) {
  if (score == null) return <span className="text-fg-subtle text-xs">—</span>;
  const tone =
    score <= 10 ? "high" :
    score <= 29 ? "suspicious" :
    score <= 59 ? "watch" :
    "good";
  return <Badge tone={tone}>{score}</Badge>;
}
